<h1 mat-dialog-title class="remove-device-dialog-header">
	@if (currentStep === DialogStep.SelectAction) {
		<span i18n="@@removeDeviceDialog-title">Remove Device</span>
	}
	@if (currentStep === DialogStep.SelectDevice) {
		<span i18n="@@removeDeviceDialog-selectDeviceTitle">Select Device for Transfer</span>
	}
	@if (currentStep === DialogStep.SendHaspCode) {
		<span i18n="@@removeDeviceDialog-sendHaspCodeTitle">Send Hasp Code</span>
	}
	@if (currentStep === DialogStep.TransferComplete) {
		<span i18n="@@removeDeviceDialog-transferCompleteTitle">Transfer Complete</span>
	}
</h1>
<hr class="remove-device-dialog-divider" />

<div mat-dialog-content class="remove-device-dialog-content">
	<!-- Step 1: Action Selection -->
	@if (currentStep === DialogStep.SelectAction) {
		<div>
			<p class="remove-device-dialog-question" i18n="@@removeDeviceDialog-question">
				What would you like to do with the user and company access for this device?
			</p>
			<div class="remove-device-dialog-options">
				<div
					class="remove-device-dialog-option"
					[class.selected]="selectedAction === DeviceRemovalActions.requestEndAllAccesses"
					(click)="onActionSelected(DeviceRemovalActions.requestEndAllAccesses)"
					data-automation-id="remove-device-end-all-access-option"
				>
					<div class="radio-button" [class.selected]="selectedAction === DeviceRemovalActions.requestEndAllAccesses">
						@if (selectedAction === DeviceRemovalActions.requestEndAllAccesses) {
							<div class="radio-inner"></div>
						}
					</div>
					<span class="option-text" i18n="@@removeDeviceDialog-endAllAccess"
						>End all user and company access to this device</span
					>
				</div>
				<div
					class="remove-device-dialog-option"
					[class.selected]="selectedAction === DeviceRemovalActions.request"
					(click)="onActionSelected(DeviceRemovalActions.request)"
					data-automation-id="remove-device-keep-current-access-option"
				>
					<div class="radio-button" [class.selected]="selectedAction === DeviceRemovalActions.request">
						@if (selectedAction === DeviceRemovalActions.request) {
							<div class="radio-inner"></div>
						}
					</div>
					<span class="option-text" i18n="@@removeDeviceDialog-keepCurrentAccess">Keep current access permissions</span>
				</div>
				<div
					class="remove-device-dialog-option"
					[class.selected]="selectedAction === DeviceRemovalActions.requestTransferAccesses"
					(click)="onActionSelected(DeviceRemovalActions.requestTransferAccesses)"
					data-automation-id="remove-device-transfer-access-option"
				>
					<div class="radio-button" [class.selected]="selectedAction === DeviceRemovalActions.requestTransferAccesses">
						@if (selectedAction === DeviceRemovalActions.requestTransferAccesses) {
							<div class="radio-inner"></div>
						}
					</div>
					<span class="option-text" i18n="@@removeDeviceDialog-transferAccess"
						>Transfer all access to another device</span
					>
				</div>
			</div>
			<div class="remove-device-dialog-warning">
				<div class="warning-icon">
					<mat-icon svgIcon="sl-danger-triangle"></mat-icon>
				</div>
				<div class="warning-text">
					<strong i18n="@@removeDeviceDialog-warningLabel">Warning:</strong>{{ ' '
					}}<span i18n="@@removeDeviceDialog-warningText"
						>If you leave access permissions unchanged, any users and companies who previously had access will
						automatically gain access when this device is installed at its next location.</span
					>
				</div>
			</div>
		</div>
	}

	<!-- Step 2: Device Selection -->
	@if (currentStep === DialogStep.SelectDevice) {
		<div class="device-selection-step">
			<p class="device-selection-description" i18n="@@removeDeviceDialog-selectDeviceDescription">
				Choose which device should receive the user and company access from this device. This transfer will take effect
				immediately.
			</p>
			<div class="device-search-container">
				<mat-form-field appearance="outline" class="device-search-field">
					<mat-icon matPrefix svgIcon="sl-search" class="search-icon"></mat-icon>
					<input
						matInput
						[formControl]="deviceSearchControl"
						[matAutocomplete]="deviceAutocomplete"
						placeholder="Search by serial number"
						i18n-placeholder="@@removeDeviceDialog-searchPlaceholder"
						data-automation-id="device-search-input"
						autocomplete="off"
					/>
					<mat-icon matSuffix class="expand-icon">expand_more</mat-icon>
					<mat-autocomplete
						#deviceAutocomplete="matAutocomplete"
						[displayWith]="displayDeviceSerialNumber"
						(optionSelected)="onDeviceSelected($event.option.value)"
						data-automation-id="device-search-dropdown"
						class="device-autocomplete-panel"
					>
						@for (device of filteredDevices | async; track device.id) {
							<mat-option [value]="device" [attr.data-automation-id]="'device-option-' + device.serialNumber">
								{{ device.serialNumber }}
							</mat-option>
						}
						@if ((filteredDevices | async)?.length === 0) {
							<mat-option disabled class="no-results-option">
								<span i18n="@@dataSetTable-noRecordsFound">No records found.</span>
							</mat-option>
						}
					</mat-autocomplete>
				</mat-form-field>
			</div>
		</div>
	}

	<!-- Step 3: Send Hasp Code -->
	@if (currentStep === DialogStep.SendHaspCode) {
		<div class="hasp-code-step">
			<p class="hasp-code-description" i18n="@@removeDeviceDialog-haspCodeDescription">
				Enter the name and email address of the person who should receive the hasp code.
			</p>
			<div class="hasp-code-form">
				<div class="hasp-code-name-row">
					<mat-form-field appearance="outline" class="hasp-code-field hasp-code-field-half">
						<input
							matInput
							[formControl]="haspFirstNameControl"
							placeholder="First Name*"
							i18n-placeholder="@@removeDeviceDialog-firstNamePlaceholder"
							data-automation-id="hasp-first-name-input"
							autocomplete="off"
						/>
					</mat-form-field>
					<mat-form-field appearance="outline" class="hasp-code-field hasp-code-field-half">
						<input
							matInput
							[formControl]="haspLastNameControl"
							placeholder="Last Name*"
							i18n-placeholder="@@removeDeviceDialog-lastNamePlaceholder"
							data-automation-id="hasp-last-name-input"
							autocomplete="off"
						/>
					</mat-form-field>
				</div>
				<mat-form-field appearance="outline" class="hasp-code-field hasp-code-field-full">
					<input
						matInput
						[formControl]="haspEmailControl"
						type="email"
						placeholder="Email"
						i18n-placeholder="@@removeDeviceDialog-emailPlaceholder"
						data-automation-id="hasp-email-input"
						autocomplete="off"
					/>
				</mat-form-field>
			</div>
		</div>
	}

	<!-- Step 4: Transfer Complete -->
	@if (currentStep === DialogStep.TransferComplete) {
		<div class="transfer-complete-step">
			<p class="transfer-complete-message">
				<span i18n="@@removeDeviceDialog-accessTransferredMessage">Access has been successfully transferred to</span>
				{{ ' ' }}<strong>{{ transferredDeviceSerialNumber }}</strong
				><span>.</span>
			</p>
			@if (sourceDeviceHaspCode) {
				<div class="hasp-code-display-container">
					<div class="row">
						<span class="hasp-code-title" i18n="@@removeDeviceDialog-oldDeviceHaspCodeIntro">Old Device:</span>
						<span class="hasp-code-text">{{ this.data.deviceName }} | </span>
						<span class="hasp-code-text">{{ sourceDeviceType }}</span>
					</div>
					<div class="hasp-code-display">
						<mat-icon svgIcon="sl-hasp-code-gen-filled" class="hasp-code-icon"></mat-icon>
						<span class="hasp-code-value">{{ sourceDeviceHaspCode }}</span>
						<button
							class="hasp-code-copy-button"
							(click)="copySourceDeviceHaspCode()"
							data-automation-id="copy-source-hasp-code-button"
						>
							<mat-icon>content_copy</mat-icon>
						</button>
					</div>
					@if (sourceDeviceHaspCodeCopied) {
						<p class="hasp-code-copied-message" i18n="@@removeDeviceDialog-codeCopiedToClipboard">
							Code copied to clipboard!
						</p>
					}
				</div>
			}
			@if (targetDeviceHaspCode) {
				<div class="hasp-code-display-container top-spacing">
					<div class="row">
						<span class="hasp-code-title" i18n="@@removeDeviceDialog-newDeviceHaspCodeIntro">New Device:</span>
						<span class="hasp-code-text">{{ transferredDeviceSerialNumber }} | </span>
						<span class="hasp-code-text">{{ targetDeviceType }}</span>
					</div>
					<div class="hasp-code-display">
						<mat-icon svgIcon="sl-hasp-code-gen-filled" class="hasp-code-icon"></mat-icon>
						<span class="hasp-code-value">{{ targetDeviceHaspCode }}</span>
						<button
							class="hasp-code-copy-button"
							(click)="copyTargetDeviceHaspCode()"
							data-automation-id="copy-target-hasp-code-button"
						>
							<mat-icon>content_copy</mat-icon>
						</button>
					</div>
					@if (targetDeviceHaspCodeCopied) {
						<p class="hasp-code-copied-message" i18n="@@removeDeviceDialog-codeCopiedToClipboard">
							Code copied to clipboard!
						</p>
					}
				</div>
			}
		</div>
	}
</div>

<div
	mat-dialog-actions
	class="remove-device-dialog-actions"
	[class.single-button]="currentStep === DialogStep.TransferComplete"
>
	@if (currentStep !== DialogStep.TransferComplete) {
		<button
			class="secondary-small-button"
			(click)="onCancel()"
			[disabled]="isLoading"
			data-automation-id="remove-device-cancel-button"
		>
			@if (currentStep === DialogStep.SelectAction) {
				<span i18n="@@removeDeviceDialog-cancelButton">Cancel</span>
			}
			@if (currentStep === DialogStep.SelectDevice || currentStep === DialogStep.SendHaspCode) {
				<span i18n="@@removeDeviceDialog-backButton">Back</span>
			}
		</button>
	}
	<button
		class="primary-small-button"
		(click)="onContinue()"
		[disabled]="isContinueDisabled()"
		data-automation-id="remove-device-continue-button"
	>
		<span class="button-text" [class.loading]="isLoading">{{ getContinueButtonText() }}</span>
		@if (isLoading) {
			<mat-spinner diameter="16" class="button-spinner" color="accent"></mat-spinner>
		}
	</button>
</div>
